import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { 
  FileText, CheckCircle, Clock, RefreshCw, ChevronDown, 
  AlertCircle, CreditCard, X, Wallet, Tag, Filter, Search,
  Building2, Wifi, Film, Shield, Home, Dumbbell, Car, MoreHorizontal,
  Eye, EyeOff, Check
} from "lucide-react";
import Context from "../../../Context";
import { auth, db } from "../../../firebase/config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { apiFetch } from "../../../utils/api";
import styles from "./Bills.module.scss";

// Types
interface DetectedBill {
  merchant: string;
  category: string;
  averageAmount: number;
  minAmount: number;
  maxAmount: number;
  frequency: string;
  confidenceScore: number;
  confidenceReason: string;
  occurrences: number;
  typicalDayOfMonth: number | null;
  dateVariance: number;
  accountIds: string[];
  description: string;
  actualOccurrences: number;
  actualMinAmount: number;
  actualMaxAmount: number;
  actualAverageAmount: number;
  dateRange: { min: number; max: number } | null;
  examples: Array<{
    id: string;
    name: string;
    amount: number;
    date: string;
    merchant: string | null;
    account_id: string;
  }>;
}

interface AccountInfo {
  account_id: string;
  name: string;
  mask: string;
  type: string;
  subtype: string;
}

type CategoryFilter = "all" | "Streaming" | "Utilities" | "Internet & Phone" | "Insurance" | "Housing" | "Subscriptions" | "Loan Payment" | "Gym & Fitness" | "Transportation" | "Other Bills";
type BillStatusFilter = "all" | "upcoming" | "paid";

const Bills: React.FC = () => {
  const { accessToken, linkSuccess } = useContext(Context);
  
  // Core data states
  const [detectedBills, setDetectedBills] = useState<DetectedBill[]>([]);
  const [accounts, setAccounts] = useState<AccountInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastDetection, setLastDetection] = useState<string | null>(null);
  
  // Filter states
  const [billStatusFilter, setBillStatusFilter] = useState<BillStatusFilter>("all");
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>("all");
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [tempSelectedAccounts, setTempSelectedAccounts] = useState<string[]>([]);
  
  // Dropdown visibility
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [pendingBills, setPendingBills] = useState<DetectedBill[]>([]);
  const [selectedBillIds, setSelectedBillIds] = useState<Set<string>>(new Set());
  const [expandedModalBillId, setExpandedModalBillId] = useState<string | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalAccountFilter, setModalAccountFilter] = useState<string[]>([]);
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [showModalFilters, setShowModalFilters] = useState(false);
  
  // Expanded bill detail
  const [expandedBillId, setExpandedBillId] = useState<string | null>(null);
  
  // Hide balances state (shared with Transactions)
  const [hideBalances, setHideBalances] = useState(false);

  // Load user preferences from Firestore (shared with Transactions)
  const loadUserPreferences = useCallback(async () => {
    try {
      if (!auth.currentUser) return;
      
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.preferences) {
          setHideBalances(data.preferences.hideBalances || false);
        }
      }
    } catch (error) {
      console.error("Error loading preferences:", error);
    }
  }, []);

  // Toggle hide balances (shared with Transactions)
  const toggleHideBalances = async () => {
    const newValue = !hideBalances;
    setHideBalances(newValue);
    
    try {
      if (!auth.currentUser) return;
      
      const userRef = doc(db, "users", auth.currentUser.uid);
      await setDoc(userRef, {
        preferences: {
          hideBalances: newValue
        }
      }, { merge: true });
    } catch (error) {
      console.error("Error saving preference:", error);
    }
  };

  // Load accounts from cache or fetch from API
  const loadAccounts = useCallback(async () => {
    try {
      if (!auth.currentUser) return;

      // Try to load from Firestore cache first
      const accountsDoc = await getDoc(doc(db, "users", auth.currentUser.uid, "accountsCache", "latest"));
      
      if (accountsDoc.exists()) {
        const data = accountsDoc.data();
        const cacheAge = new Date().getTime() - new Date(data.lastSync).getTime();
        const oneHourInMs = 60 * 60 * 1000;
        
        // Use cache if less than 1 hour old
        if (cacheAge < oneHourInMs) {
          console.log("✅ Using cached accounts data");
          setAccounts(data.accounts || []);
          return;
        }
      }

      // Cache is stale or doesn't exist - fetch from API
      console.log("🔄 Fetching accounts from API");
      const response = await apiFetch('/api/accounts');
      if (response.ok) {
        const data = await response.json();
        const accountsList = data.accounts || [];
        setAccounts(accountsList);
        
        // Save to cache
        await setDoc(
          doc(db, "users", auth.currentUser.uid, "accountsCache", "latest"),
          {
            accounts: accountsList,
            lastSync: new Date().toISOString()
          }
        );
        console.log("✅ Accounts cached to Firestore");
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  }, []);

  // Load saved bills from Firestore
  const loadSavedBills = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!auth.currentUser) {
        setLoading(false);
        return;
      }

      const billsDoc = await getDoc(doc(db, "users", auth.currentUser.uid, "detectedBills", "latest"));
      
      if (billsDoc.exists()) {
        const data = billsDoc.data();
        setDetectedBills(data.bills || []);
        setLastDetection(data.detectedAt);
        console.log(`✅ Loaded ${data.bills?.length || 0} saved bills`);
      } else {
        // No saved bills, trigger detection
        console.log("No saved bills found, triggering detection...");
        await handleDetectBills();
      }
    } catch (err) {
      console.error('Error loading bills:', err);
      setError('Failed to load bills');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    if (linkSuccess && accessToken) {
      loadUserPreferences();
      loadSavedBills();
      loadAccounts();
    }
  }, [linkSuccess, accessToken, loadUserPreferences, loadSavedBills, loadAccounts]);

  // Bill Detection using Algorithm
  const handleDetectBills = async () => {
    try {
      // Show modal immediately with loading state
      setShowSelectionModal(true);
      setModalLoading(true);
      setPendingBills([]);
      setSelectedBillIds(new Set());
      setModalAccountFilter([]);
      setModalSearchQuery('');
      setDetecting(true);
      setError(null);

      console.log('🔍 Starting bill detection...');

      const response = await apiFetch('/api/bills/algorithm-detect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ months: 12, minOccurrences: 2 })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Detection failed');
      }

      const data = await response.json();
      console.log(`✅ Detected ${data.billsFound} bills`);

      // Update modal with detected bills
      setPendingBills(data.detectedBills);
      
      // Pre-select high confidence bills
      const highConfidenceBills = new Set<string>(
        data.detectedBills
          .filter((bill: DetectedBill) => bill.confidenceScore >= 70)
          .map((bill: DetectedBill) => `${bill.merchant}-${bill.category}`)
      );
      setSelectedBillIds(highConfidenceBills);
    } catch (err) {
      console.error('❌ Detection error:', err);
      setError(err instanceof Error ? err.message : 'Detection failed');
      setShowSelectionModal(false); // Close modal on error
    } finally {
      setDetecting(false);
      setModalLoading(false);
    }
  };

  // Get primary account for a bill (the account with most transactions)
  const getPrimaryAccount = useCallback((bill: DetectedBill): string => {
    if (!bill.examples || bill.examples.length === 0) return 'Unknown';
    
    // Count transactions by account
    const accountCounts: Record<string, number> = {};
    bill.examples.forEach(ex => {
      accountCounts[ex.account_id] = (accountCounts[ex.account_id] || 0) + 1;
    });
    
    // Find account with most transactions
    const primaryAccountId = Object.entries(accountCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
    const account = accounts.find(a => a.account_id === primaryAccountId);
    return account ? `${account.name} (...${account.mask})` : 'Unknown';
  }, [accounts]);

  // Get account color class based on account ID
  const getAccountColor = useCallback((bill: DetectedBill): string => {
    if (!bill.examples || bill.examples.length === 0) return 'color0';
    
    // Count transactions by account
    const accountCounts: Record<string, number> = {};
    bill.examples.forEach(ex => {
      accountCounts[ex.account_id] = (accountCounts[ex.account_id] || 0) + 1;
    });
    
    // Find account with most transactions
    const primaryAccountId = Object.entries(accountCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
    const accountIndex = accounts.findIndex(a => a.account_id === primaryAccountId);
    
    // Return color class based on account index (cycle through 6 colors)
    return `color${accountIndex >= 0 ? accountIndex % 6 : 0}`;
  }, [accounts]);

  // Filter bills
  const filteredBills = useMemo(() => {
    const today = new Date();
    const currentDay = today.getDate();
    
    return detectedBills.filter(bill => {
      // Status filter
      if (billStatusFilter !== "all") {
        const isUpcoming = bill.typicalDayOfMonth && bill.typicalDayOfMonth >= currentDay;
        if (billStatusFilter === "upcoming" && !isUpcoming) return false;
        if (billStatusFilter === "paid" && isUpcoming) return false;
      }
      
      // Category filter
      if (selectedCategory !== "all" && bill.category !== selectedCategory) {
        return false;
      }
      
      // Account filter
      if (selectedAccounts.length > 0) {
        const billAccountIds = bill.accountIds || bill.examples?.map(e => e.account_id) || [];
        const hasMatchingAccount = billAccountIds.some(id => selectedAccounts.includes(id));
        if (!hasMatchingAccount) return false;
      }
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesMerchant = bill.merchant.toLowerCase().includes(query);
        const matchesCategory = bill.category.toLowerCase().includes(query);
        if (!matchesMerchant && !matchesCategory) return false;
      }
      
      return true;
    });
  }, [detectedBills, billStatusFilter, selectedCategory, selectedAccounts, searchQuery]);

  // Calculate totals
  const totalMonthly = useMemo(() => {
    return filteredBills.reduce((sum, bill) => {
      const amount = bill.actualAverageAmount || bill.averageAmount || 0;
      if (bill.frequency === 'monthly') return sum + amount;
      if (bill.frequency === 'weekly') return sum + (amount * 4);
      if (bill.frequency === 'bi-weekly') return sum + (amount * 2);
      if (bill.frequency === 'quarterly') return sum + (amount / 3);
      if (bill.frequency === 'annual') return sum + (amount / 12);
      return sum + amount;
    }, 0);
  }, [filteredBills]);

  // Get unique categories from bills
  const uniqueCategories = useMemo(() => {
    const cats = new Set(detectedBills.map(b => b.category));
    return Array.from(cats).sort();
  }, [detectedBills]);

  // Get unique accounts that have bills
  const uniqueBillAccounts = useMemo(() => {
    const accountIds = new Set<string>();
    detectedBills.forEach(bill => {
      const billAccountIds = bill.accountIds || bill.examples?.map(e => e.account_id) || [];
      billAccountIds.forEach(id => accountIds.add(id));
    });
    return accounts.filter(acc => accountIds.has(acc.account_id));
  }, [detectedBills, accounts]);

  // Get account color for a specific account ID (using same logic as getAccountColor)
  const getAccountColorById = useCallback((accountId: string): string => {
    const accountIndex = accounts.findIndex(acc => acc.account_id === accountId);
    return `color${accountIndex >= 0 ? accountIndex % 6 : 0}`;
  }, [accounts]);

  // Get upcoming bills for the current month
  const upcomingBills = useMemo(() => {
    const today = new Date();
    const currentDay = today.getDate();
    
    return filteredBills.filter(bill => {
      const dueDay = bill.typicalDayOfMonth;
      if (!dueDay) return false;
      return dueDay >= currentDay;
    }).sort((a, b) => (a.typicalDayOfMonth || 0) - (b.typicalDayOfMonth || 0));
  }, [filteredBills]);

  // Get past bills for the current month
  const paidBills = useMemo(() => {
    const today = new Date();
    const currentDay = today.getDate();
    
    return filteredBills.filter(bill => {
      const dueDay = bill.typicalDayOfMonth;
      if (!dueDay) return true; // If no due day, show as paid
      return dueDay < currentDay;
    }).sort((a, b) => (b.typicalDayOfMonth || 0) - (a.typicalDayOfMonth || 0));
  }, [filteredBills]);

  // Toggle account selection
  const toggleAccountSelection = (accountId: string) => {
    setTempSelectedAccounts(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      } else {
        return [...prev, accountId];
      }
    });
  };

  // Apply account selection
  const applyAccountSelection = () => {
    setSelectedAccounts(tempSelectedAccounts);
    setShowAccountDropdown(false);
  };

  // Get category icon
  const getCategoryIcon = (category: string) => {
    const icons: Record<string, React.ReactNode> = {
      'Streaming': <Film size={18} />,
      'Utilities': <Building2 size={18} />,
      'Internet & Phone': <Wifi size={18} />,
      'Insurance': <Shield size={18} />,
      'Housing': <Home size={18} />,
      'Subscriptions': <CreditCard size={18} />,
      'Loan Payment': <FileText size={18} />,
      'Gym & Fitness': <Dumbbell size={18} />,
      'Transportation': <Car size={18} />,
      'Other Bills': <MoreHorizontal size={18} />
    };
    return icons[category] || <FileText size={18} />;
  };

  // Get confidence color
  const getConfidenceColor = (score: number) => {
    if (score >= 80) return '#10b981'; // Green
    if (score >= 50) return '#f59e0b'; // Amber
    return '#ef4444'; // Red
  };

  // Get confidence label
  const getConfidenceLabel = (score: number) => {
    if (score >= 80) return 'High';
    if (score >= 50) return 'Medium';
    return 'Low';
  };

  // Format date range for bill due date
  const formatDueDateRange = (bill: DetectedBill) => {
    const day = bill.typicalDayOfMonth;
    const variance = bill.dateVariance || 0;
    
    if (!day) return 'Variable';
    
    const today = new Date();
    const month = today.toLocaleDateString('en-US', { month: 'short' });
    
    if (variance === 0) {
      return `${month} ${day}`;
    } else {
      const minDay = Math.max(1, day - variance);
      const maxDay = Math.min(31, day + variance);
      return `${month} ${minDay}-${maxDay}`;
    }
  };

  // Format frequency
  const formatFrequency = (freq: string) => {
    const labels: Record<string, string> = {
      'monthly': 'Monthly',
      'weekly': 'Weekly',
      'bi-weekly': 'Bi-weekly',
      'quarterly': 'Quarterly',
      'annual': 'Annual'
    };
    return labels[freq] || freq;
  };

  // Format amount range
  const formatAmountRange = (bill: DetectedBill) => {
    if (hideBalances) return "••••";
    
    const min = bill.actualMinAmount || bill.minAmount || bill.averageAmount;
    const max = bill.actualMaxAmount || bill.maxAmount || bill.averageAmount;
    
    if (Math.abs(min - max) < 0.01) {
      return `$${min.toFixed(2)}`;
    }
    return `$${min.toFixed(2)} - $${max.toFixed(2)}`;
  };

  // Modal functions
  const toggleBillSelection = (billId: string) => {
    const newSelected = new Set(selectedBillIds);
    if (newSelected.has(billId)) {
      newSelected.delete(billId);
    } else {
      newSelected.add(billId);
    }
    setSelectedBillIds(newSelected);
  };

  const toggleModalBillExpansion = (billId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent selection toggle
    setExpandedModalBillId(prev => prev === billId ? null : billId);
  };

  const toggleSelectAll = () => {
    if (selectedBillIds.size === pendingBills.length) {
      // Deselect all
      setSelectedBillIds(new Set());
    } else {
      // Select all
      const allBillIds = pendingBills.map(bill => 
        `${bill.merchant}-${bill.category}-${(bill.actualAverageAmount || bill.averageAmount || 0).toFixed(2)}`
      );
      setSelectedBillIds(new Set(allBillIds));
    }
  };

  const selectHighConfidence = () => {
    // Filter bills by search and account filter first
    const visibleBills = pendingBills.filter(bill => {
      // Search filter
      if (modalSearchQuery) {
        const query = modalSearchQuery.toLowerCase();
        if (!bill.merchant.toLowerCase().includes(query) && 
            !bill.category.toLowerCase().includes(query)) {
          return false;
        }
      }
      // Account filter
      if (modalAccountFilter.length > 0) {
        const billAccounts = bill.accountIds || bill.examples?.map(e => e.account_id) || [];
        if (!billAccounts.some(id => modalAccountFilter.includes(id))) {
          return false;
        }
      }
      return true;
    });

    // Select bills with confidence score >= 90 (out of 100)
    const highConfidenceBills = visibleBills.filter(bill => {
      const score = bill.confidenceScore || 0;
      return score >= 90;
    });
    
    const highConfidenceBillIds = highConfidenceBills.map(bill => 
      `${bill.merchant}-${bill.category}-${(bill.actualAverageAmount || bill.averageAmount || 0).toFixed(2)}`
    );
    
    console.log(`Selecting ${highConfidenceBills.length} high confidence bills (>=90) out of ${visibleBills.length} visible bills`);
    console.log('High confidence bills:', highConfidenceBills.map(b => ({ merchant: b.merchant, score: b.confidenceScore })));
    
    setSelectedBillIds(new Set(highConfidenceBillIds));
  };

  const saveSelectedBills = async () => {
    try {
      // Filter bills based on selection
      const selectedBills = pendingBills.filter(bill => {
        const billId = `${bill.merchant}-${bill.category}-${(bill.actualAverageAmount || bill.averageAmount || 0).toFixed(2)}`;
        return selectedBillIds.has(billId);
      });

      // Save to Firestore and state
      if (auth.currentUser) {
        await setDoc(
          doc(db, "users", auth.currentUser.uid, "detectedBills", "latest"),
          {
            bills: selectedBills,
            detectedAt: new Date().toISOString(),
          }
        );
      }

      setDetectedBills(selectedBills);
      setLastDetection(new Date().toISOString());
      setShowSelectionModal(false);
      setPendingBills([]);
      setSelectedBillIds(new Set());
    } catch (err) {
      console.error('Error saving bills:', err);
      setError('Failed to save selected bills');
    }
  };

  const cancelSelection = () => {
    setShowSelectionModal(false);
    setPendingBills([]);
    setSelectedBillIds(new Set());
    setExpandedModalBillId(null);
    setModalLoading(false);
    setModalAccountFilter([]);
    setModalSearchQuery('');
  };

  // Toggle bill expansion
  const toggleBillExpansion = (billId: string) => {
    setExpandedBillId(prev => prev === billId ? null : billId);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if click is outside the filter dropdown container
      const filterDropdown = target.closest('[data-dropdown="filter"]');
      if (!filterDropdown) {
        setShowCategoryDropdown(false);
        setShowAccountDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Initialize temp accounts when dropdown opens
  useEffect(() => {
    if (showAccountDropdown) {
      setTempSelectedAccounts(selectedAccounts);
    }
  }, [showAccountDropdown, selectedAccounts]);

  // Loading state
  if (loading) {
    return (
      <div className={styles.bills}>
        <div className={styles.loadingContainer}>
          <RefreshCw className={styles.spinning} size={48} />
          <p>Loading your bills...</p>
        </div>
      </div>
    );
  }

  // No bank connected
  if (!linkSuccess || !accessToken) {
    return (
      <div className={styles.bills}>
        <div className={styles.emptyState}>
          <FileText size={48} />
          <h3>No Bank Connected</h3>
          <p>Connect your bank account from the Profile page to detect bills.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.bills}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1>Bills & Subscriptions</h1>
          {lastDetection && (
            <span className={styles.lastSync}>
              Last analyzed: {new Date(lastDetection).toLocaleDateString()}
            </span>
          )}
        </div>
        <button onClick={toggleHideBalances} className={styles.toggleBalanceBtn} title={hideBalances ? "Show balances" : "Hide balances"}>
          {hideBalances ? <EyeOff size={20} /> : <Eye size={20} />}
          <span>{hideBalances ? "Show" : "Hide"} Balances</span>
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className={styles.errorBanner}>
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Summary Cards */}
      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.cardIcon} style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
            <FileText size={24} />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>
              {billStatusFilter === "all" && "Monthly Bills"}
              {billStatusFilter === "upcoming" && "Upcoming Bills"}
              {billStatusFilter === "paid" && "Paid Bills"}
            </span>
            <span className={styles.cardValue}>{hideBalances ? "••••••" : `$${totalMonthly.toFixed(2)}`}</span>
          </div>
        </div>
        
        <div className={styles.summaryCard}>
          <div className={styles.cardIcon} style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
            <Clock size={24} />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>Upcoming This Month</span>
            <span className={styles.cardValue}>{upcomingBills.length} bills</span>
          </div>
        </div>
        
        <div className={styles.summaryCard}>
          <div className={styles.cardIcon} style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
            <CheckCircle size={24} />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>Paid This Month</span>
            <span className={styles.cardValue}>{paidBills.length} bills</span>
          </div>
        </div>
        
        <div className={styles.summaryCard}>
          <div className={styles.cardIcon} style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
            <FileText size={24} />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>Bills Detected</span>
            <span className={styles.cardValue}>{detectedBills.length} total</span>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className={styles.filterBar}>
        {/* Status Filter Tabs */}
        <div className={styles.typeFilters}>
          <button 
            className={`${styles.filterTab} ${billStatusFilter === "all" ? styles.active : ""}`}
            onClick={() => setBillStatusFilter("all")}
          >
            All
          </button>
          <button 
            className={`${styles.filterTab} ${billStatusFilter === "upcoming" ? styles.active : ""}`}
            onClick={() => setBillStatusFilter("upcoming")}
          >
            <Clock size={16} />
            Upcoming
          </button>
          <button 
            className={`${styles.filterTab} ${billStatusFilter === "paid" ? styles.active : ""}`}
            onClick={() => setBillStatusFilter("paid")}
          >
            <CheckCircle size={16} />
            Paid
          </button>
        </div>

        {/* Category Filter */}
        <div className={styles.filterDropdown} data-dropdown="filter">
          <button 
            type="button"
            className={styles.categoryTrigger}
            onClick={(e) => {
              console.log('Category trigger clicked, current state:', showCategoryDropdown);
              e.preventDefault();
              e.stopPropagation();
              setShowCategoryDropdown(!showCategoryDropdown);
              setShowAccountDropdown(false);
            }}
          >
            <Tag size={20} />
            <span>{selectedCategory === "all" ? "All Categories" : selectedCategory}</span>
            <ChevronDown size={18} className={showCategoryDropdown ? styles.rotated : ''} />
          </button>
          {showCategoryDropdown && (
            <div className={styles.dropdownMenu}>
              <button 
                type="button"
                className={`${styles.dropdownItem} ${selectedCategory === "all" ? styles.active : ""}`}
                onClick={(e) => { console.log('All Categories clicked'); e.stopPropagation(); setSelectedCategory("all"); setShowCategoryDropdown(false); }}
              >
                <Tag size={18} />
                <span>All Categories</span>
                {selectedCategory === "all" && <span className={styles.checkmark}>✓</span>}
              </button>
              {uniqueCategories.map(cat => (
                <button 
                  type="button"
                  key={cat}
                  className={`${styles.dropdownItem} ${selectedCategory === cat ? styles.active : ""}`}
                  onClick={(e) => { console.log('Category clicked:', cat); e.stopPropagation(); setSelectedCategory(cat as CategoryFilter); setShowCategoryDropdown(false); }}
                >
                  {getCategoryIcon(cat)}
                  <span>{cat}</span>
                  {selectedCategory === cat && <span className={styles.checkmark}>✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Account Filter */}
        <div className={styles.filterDropdown} data-dropdown="filter">
          <button 
            type="button"
            className={styles.accountTrigger}
            onClick={(e) => {
              console.log('Account trigger clicked, current state:', showAccountDropdown);
              e.preventDefault();
              e.stopPropagation();
              setShowAccountDropdown(!showAccountDropdown);
              setShowCategoryDropdown(false);
            }}
          >
            <Wallet size={20} />
            <span>
              {selectedAccounts.length === 0 
                ? "All Accounts" 
                : `${selectedAccounts.length} Account${selectedAccounts.length > 1 ? 's' : ''}`}
            </span>
            <ChevronDown size={18} className={showAccountDropdown ? styles.rotated : ''} />
          </button>
          {showAccountDropdown && (
            <div className={styles.dropdownMenu}>
              <button
                type="button"
                className={`${styles.dropdownItem} ${tempSelectedAccounts.length === 0 ? styles.active : ''}`}
                onClick={(e) => {
                  console.log('All Accounts clicked');
                  e.stopPropagation();
                  setTempSelectedAccounts([]);
                  setSelectedAccounts([]);
                  setShowAccountDropdown(false);
                }}
              >
                <Wallet size={18} />
                <span>All Accounts</span>
                {tempSelectedAccounts.length === 0 && <span className={styles.checkmark}>✓</span>}
              </button>
              {uniqueBillAccounts.map(account => {
                const colorClass = getAccountColorById(account.account_id);
                return (
                  <div 
                    key={account.account_id}
                    className={styles.accountItemWithCheckbox}
                    onClick={(e) => { console.log('Account checkbox clicked:', account.name); e.stopPropagation(); toggleAccountSelection(account.account_id); }}
                  >
                    <div className={`${styles.accountCheckbox} ${tempSelectedAccounts.includes(account.account_id) ? styles.checked : ''}`}>
                      {tempSelectedAccounts.includes(account.account_id) && <Check size={14} />}
                    </div>
                    <span className={`${styles.accountBadge} ${styles[colorClass]}`}>
                      {account.name} (••••{account.mask})
                    </span>
                  </div>
                );
              })}
              {tempSelectedAccounts.length > 0 && (
                <button 
                  type="button"
                  className={styles.confirmAccountBtn}
                  onClick={(e) => { console.log('Apply Filter clicked, accounts:', tempSelectedAccounts); e.stopPropagation(); applyAccountSelection(); }}
                >
                  Apply Filter
                </button>
              )}
            </div>
          )}
        </div>

        {/* Actions - Filter Toggle & Re-detect */}
        <div className={styles.filterActions}>
          <button 
            className={styles.filterToggle}
            onClick={() => setShowFilters(!showFilters)}
            title="Toggle search"
          >
            <Filter size={18} />
          </button>
          <button 
            className={styles.refreshBtn}
            onClick={handleDetectBills}
            disabled={detecting}
            title="Re-detect bills"
          >
            <RefreshCw size={18} className={detecting ? styles.spinning : ""} />
          </button>
        </div>
      </div>

      {/* Expanded Filters - Search Bar */}
      {showFilters && (
        <div className={styles.expandedFilters}>
          <div className={styles.searchWrapper}>
            <Search size={22} />
            <input
              type="text"
              placeholder="Search bills by name or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
              autoFocus
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className={styles.clearSearch}>
                <X size={18} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Bills List */}
      <div className={styles.billsSection}>
        {/* Upcoming Bills */}
        {upcomingBills.length > 0 && (
          <div className={styles.billsGroup}>
            <div className={styles.groupHeader}>
              <Clock size={18} />
              <span>Upcoming This Month</span>
              <span className={styles.groupCount}>{upcomingBills.length}</span>
            </div>
            <div className={styles.billsList}>
              {upcomingBills.map(bill => {
                const billId = `${bill.merchant}-${bill.category}-${(bill.actualAverageAmount || bill.averageAmount || 0).toFixed(2)}`;
                const isExpanded = expandedBillId === billId;
                
                return (
                  <div 
                    key={billId} 
                    className={`${styles.billItemWrapper} ${isExpanded ? styles.expanded : ''}`}
                  >
                    <div 
                      className={styles.billItem}
                      onClick={() => toggleBillExpansion(billId)}
                    >
                      <div className={styles.billIcon} style={{ background: `${getConfidenceColor(bill.confidenceScore)}20`, color: getConfidenceColor(bill.confidenceScore) }}>
                        {getCategoryIcon(bill.category)}
                      </div>
                      <div className={styles.billInfo}>
                        <span className={styles.billName}>{bill.merchant}</span>
                        <span className={styles.billMeta}>
                          {bill.category} • {formatFrequency(bill.frequency)} • Due {formatDueDateRange(bill)}
                        </span>
                      </div>
                      <div className={styles.billRight}>
                        <span className={styles.billAmount}>{hideBalances ? "••••" : `$${(bill.actualAverageAmount || bill.averageAmount || 0).toFixed(2)}`}</span>
                        <span className={`${styles.accountBadge} ${styles[getAccountColor(bill)]}`}>
                          {getPrimaryAccount(bill)}
                        </span>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className={styles.billExpanded}>
                        <div className={styles.expandedDetails}>
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Amount Range</span>
                            <span className={styles.detailValue}>{formatAmountRange(bill)}</span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Occurrences</span>
                            <span className={styles.detailValue}>{bill.actualOccurrences || bill.occurrences} times detected</span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Confidence</span>
                            <span className={styles.detailValue}>{bill.confidenceReason || `${bill.confidenceScore}% confidence`}</span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Date Variance</span>
                            <span className={styles.detailValue}>±{bill.dateVariance || 0} days</span>
                          </div>
                          {bill.description && (
                            <div className={styles.detailItem}>
                              <span className={styles.detailLabel}>Description</span>
                              <span className={styles.detailValue}>{bill.description}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Recent Examples */}
                        {bill.examples && bill.examples.length > 0 && (
                          <div className={styles.examplesSection}>
                            <span className={styles.examplesTitle}>Recent Transactions</span>
                            <div className={styles.examplesList}>
                              {bill.examples.slice(0, 4).map((ex, idx) => (
                                <div key={ex.id || idx} className={styles.exampleItem}>
                                  <span className={styles.exampleDate}>
                                    {new Date(ex.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </span>
                                  <span className={styles.exampleName}>{ex.name}</span>
                                  <span className={styles.exampleAmount}>{hideBalances ? "••••" : `$${ex.amount.toFixed(2)}`}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Paid Bills */}
        {paidBills.length > 0 && (
          <div className={styles.billsGroup}>
            <div className={styles.groupHeader}>
              <CheckCircle size={18} />
              <span>Paid This Month</span>
              <span className={styles.groupCount}>{paidBills.length}</span>
            </div>
            <div className={styles.billsList}>
              {paidBills.map(bill => {
                const billId = `${bill.merchant}-${bill.category}-${(bill.actualAverageAmount || bill.averageAmount || 0).toFixed(2)}`;
                const isExpanded = expandedBillId === billId;
                
                return (
                  <div 
                    key={billId} 
                    className={`${styles.billItemWrapper} ${isExpanded ? styles.expanded : ''}`}
                  >
                    <div 
                      className={styles.billItem}
                      onClick={() => toggleBillExpansion(billId)}
                    >
                      <div className={styles.billIcon} style={{ background: `${getConfidenceColor(bill.confidenceScore)}20`, color: getConfidenceColor(bill.confidenceScore) }}>
                        {getCategoryIcon(bill.category)}
                      </div>
                      <div className={styles.billInfo}>
                        <span className={styles.billName}>{bill.merchant}</span>
                        <span className={styles.billMeta}>
                          {bill.category} • {formatFrequency(bill.frequency)} • Paid {formatDueDateRange(bill)}
                        </span>
                      </div>
                      <div className={styles.billRight}>
                        <span className={styles.billAmount}>{hideBalances ? "••••" : `$${(bill.actualAverageAmount || bill.averageAmount || 0).toFixed(2)}`}</span>
                        <span className={`${styles.accountBadge} ${styles[getAccountColor(bill)]}`}>
                          {getPrimaryAccount(bill)}
                        </span>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className={styles.billExpanded}>
                        <div className={styles.expandedDetails}>
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Amount Range</span>
                            <span className={styles.detailValue}>{formatAmountRange(bill)}</span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Occurrences</span>
                            <span className={styles.detailValue}>{bill.actualOccurrences || bill.occurrences} times detected</span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Confidence</span>
                            <span className={styles.detailValue}>{bill.confidenceReason || `${bill.confidenceScore}% confidence`}</span>
                          </div>
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Date Variance</span>
                            <span className={styles.detailValue}>±{bill.dateVariance || 0} days</span>
                          </div>
                          {bill.description && (
                            <div className={styles.detailItem}>
                              <span className={styles.detailLabel}>Description</span>
                              <span className={styles.detailValue}>{bill.description}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Recent Examples */}
                        {bill.examples && bill.examples.length > 0 && (
                          <div className={styles.examplesSection}>
                            <span className={styles.examplesTitle}>Recent Transactions</span>
                            <div className={styles.examplesList}>
                              {bill.examples.slice(0, 4).map((ex, idx) => (
                                <div key={ex.id || idx} className={styles.exampleItem}>
                                  <span className={styles.exampleDate}>
                                    {new Date(ex.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </span>
                                  <span className={styles.exampleName}>{ex.name}</span>
                                  <span className={styles.exampleAmount}>{hideBalances ? "••••" : `$${ex.amount.toFixed(2)}`}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredBills.length === 0 && !loading && !detecting && (
          <div className={styles.emptyState}>
            <FileText size={48} />
            <h3>No Bills Found</h3>
            <p>
              {detectedBills.length === 0 
                ? "Click 'Re-detect Bills' to analyze your transactions for recurring bills."
                : "No bills match your current filters. Try adjusting your filters."}
            </p>
            {detectedBills.length === 0 && (
              <button className={styles.detectBtnLarge} onClick={handleDetectBills} disabled={detecting}>
                <RefreshCw size={20} />
                Detect Bills
              </button>
            )}
          </div>
        )}

        {/* Detecting State */}
        {detecting && (
          <div className={styles.detectingState}>
            <RefreshCw className={styles.spinning} size={48} />
            <h3>Analyzing Your Transactions...</h3>
            <p>Looking for recurring bills and subscriptions in your transaction history.</p>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} />
            </div>
          </div>
        )}
      </div>

      {/* Selection Modal */}
      {showSelectionModal && (
        <div className={styles.modalOverlay} onClick={cancelSelection}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderTop}>
                <div>
                  <h2>Select Bills to Track</h2>
                  <p>{modalLoading ? 'Analyzing your transactions...' : `We detected ${pendingBills.length} possible recurring bills. Select which ones you want to track. You can see the confidence score on the right.`}</p>
                </div>
                {!modalLoading && pendingBills.length > 0 && (
                  <button className={styles.selectAllBtn} onClick={toggleSelectAll}>
                    {selectedBillIds.size === pendingBills.length ? (
                      <>
                        <X size={18} />
                        Deselect All
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        Select All
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Loading State */}
            {modalLoading && (
              <div className={styles.modalLoadingState}>
                <RefreshCw className={styles.spinning} size={48} />
                <p>Looking for recurring bills...</p>
              </div>
            )}

            {/* Filters and Search - Only show when loaded */}
            {!modalLoading && pendingBills.length > 0 && (
              <div className={styles.modalFiltersContainer}>
                <div className={styles.filterHeader}>
                  <button 
                    className={`${styles.filterToggleBtn} ${showModalFilters ? styles.active : ''}`}
                    onClick={() => setShowModalFilters(!showModalFilters)}
                  >
                    <Filter size={16} />
                    Filters
                    {(modalSearchQuery || modalAccountFilter.length > 0) && (
                      <span className={styles.filterBadge}>
                        {(modalSearchQuery ? 1 : 0) + modalAccountFilter.length}
                      </span>
                    )}
                  </button>
                  <span className={styles.billCount}>Search and filter by account</span>
                  <button className={styles.selectSuggestedBtn} onClick={selectHighConfidence}>
                    Select Suggested (High Confidence)
                  </button>
                </div>

                {showModalFilters && (
                  <div className={styles.modalFilters}>
                    {/* Search Bar */}
                    <div className={styles.modalSearchBar}>
                  <input
                    type="text"
                    placeholder="Search bills..."
                    value={modalSearchQuery}
                    onChange={(e) => setModalSearchQuery(e.target.value)}
                    className={styles.modalSearchInput}
                  />
                  {modalSearchQuery && (
                    <button
                      className={styles.modalSearchClear}
                      onClick={() => setModalSearchQuery('')}
                      aria-label="Clear search"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>

                {/* Account Filter */}
                <div className={styles.modalAccountFilter}>
                  <label>Filter by Account:</label>
                  <div className={styles.modalAccountChips}>
                    <button
                      className={`${styles.modalAccountChip} ${modalAccountFilter.length === 0 ? styles.active : ''}`}
                      onClick={() => setModalAccountFilter([])}
                    >
                      All
                    </button>
                    {accounts.map(account => {
                      const isSelected = modalAccountFilter.includes(account.account_id);
                      return (
                        <button
                          key={account.account_id}
                          className={`${styles.modalAccountChip} ${isSelected ? styles.active : ''}`}
                          onClick={() => {
                            if (isSelected) {
                              setModalAccountFilter(prev => prev.filter(id => id !== account.account_id));
                            } else {
                              setModalAccountFilter(prev => [...prev, account.account_id]);
                            }
                          }}
                        >
                          {account.name} (...{account.mask})
                        </button>
                      );
                    })}
                  </div>
                </div>
                  </div>
                )}
              </div>
            )}

            {/* Bills List */}
            {!modalLoading && (
              <div className={styles.modalBillsList}>
                {pendingBills
                  .filter(bill => {
                    // Search filter
                    if (modalSearchQuery) {
                      const query = modalSearchQuery.toLowerCase();
                      if (!bill.merchant.toLowerCase().includes(query) && 
                          !bill.category.toLowerCase().includes(query)) {
                        return false;
                      }
                    }
                    // Account filter
                    if (modalAccountFilter.length > 0) {
                      const billAccounts = bill.accountIds || bill.examples?.map(e => e.account_id) || [];
                      if (!billAccounts.some(id => modalAccountFilter.includes(id))) {
                        return false;
                      }
                    }
                    return true;
                  })
                  .map(bill => {
                const billId = `${bill.merchant}-${bill.category}-${(bill.actualAverageAmount || bill.averageAmount || 0).toFixed(2)}`;
                const isSelected = selectedBillIds.has(billId);
                const isExpanded = expandedModalBillId === billId;
                
                return (
                  <div key={billId} className={styles.modalBillWrapper}>
                    <div 
                      className={`${styles.modalBillItem} ${isSelected ? styles.selected : ''}`}
                      onClick={() => toggleBillSelection(billId)}
                    >
                      <div className={styles.modalCheckbox}>
                        {isSelected && <CheckCircle size={20} />}
                      </div>
                      
                      <div className={styles.modalBillIcon} style={{ background: `${getConfidenceColor(bill.confidenceScore)}20`, color: getConfidenceColor(bill.confidenceScore) }}>
                        {getCategoryIcon(bill.category)}
                      </div>
                      
                      <div className={styles.modalBillInfo}>
                        <div className={styles.modalBillName}>{bill.merchant}</div>
                        <div className={styles.modalBillMeta}>
                          {bill.category} • {formatFrequency(bill.frequency)} • ${(bill.actualAverageAmount || bill.averageAmount || 0).toFixed(2)}
                        </div>
                      </div>
                      
                      <div className={`${styles.modalAccountBadge} ${styles[getAccountColor(bill)]}`}>
                        {getPrimaryAccount(bill)}
                      </div>

                      <div className={styles.modalConfidenceBadge} style={{ background: `${getConfidenceColor(bill.confidenceScore)}20`, color: getConfidenceColor(bill.confidenceScore) }}>
                        <span className={styles.confidenceLabel}>{getConfidenceLabel(bill.confidenceScore)}</span>
                        <span className={styles.confidenceScore}>{bill.confidenceScore}%</span>
                      </div>

                      {/* Expand/Collapse button for transactions */}
                      {bill.examples && bill.examples.length > 0 && (
                        <button 
                          className={styles.modalExpandBtn}
                          onClick={(e) => toggleModalBillExpansion(billId, e)}
                          title="View transactions"
                        >
                          <ChevronDown 
                            size={20} 
                            style={{ 
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease'
                            }} 
                          />
                        </button>
                      )}
                    </div>

                    {/* Expanded Transaction List */}
                    {isExpanded && bill.examples && bill.examples.length > 0 && (
                      <div className={styles.modalTransactions}>
                        <div className={styles.modalTransactionsHeader}>
                          Recent Transactions ({bill.examples.length})
                        </div>
                        <div className={styles.modalTransactionsList}>
                          {bill.examples.map((ex, idx) => (
                            <div key={ex.id || idx} className={styles.modalTransactionItem}>
                              <span className={styles.modalTransactionDate}>
                                {new Date(ex.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                              <span className={styles.modalTransactionName}>{ex.name}</span>
                              <span className={styles.modalTransactionAmount}>{hideBalances ? "••••" : `$${ex.amount.toFixed(2)}`}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            )}

            {/* Modal Actions */}
            {!modalLoading && (
              <div className={styles.modalActions}>
                <button className={styles.cancelBtn} onClick={cancelSelection}>
                  Cancel
                </button>
                <button 
                  className={styles.saveBtn} 
                  onClick={saveSelectedBills}
                  disabled={selectedBillIds.size === 0}
                >
                  <CheckCircle size={18} />
                  Save {selectedBillIds.size} Bill{selectedBillIds.size !== 1 ? 's' : ''}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Bills;
